import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Spinner } from 'react-bootstrap';
import type { RecommendationLabInputOptions, SkinMetricKey } from '@/models/RecommendationLab';
import { Currency } from '@/models/Currency';
import type { SkinAnalysisPredictValidationRequest } from '@/models/SkinAnalysisPredictValidation';
import { DEFAULT_PRESET, METRICS, scoreVariant, type LabPreset } from '@/pages/recommendation-lab/data';
import {
  buildRecommendationEnvironment,
  collectRecommendationEnvironmentIssues,
  DEFAULT_RECOMMENDATION_ENVIRONMENT,
  type RecommendationEnvironmentFormValue,
} from '@/pages/recommendation-lab/environment';
import { RecommendationEnvironmentForm } from '@/pages/recommendation-lab/RecommendationEnvironmentForm';
import { RecommendationLabCaseControls } from '@/pages/recommendation-lab/RecommendationLabCaseControls';
import { RecommendationSurveyForm } from '@/pages/recommendation-lab/RecommendationSurveyForm';
import {
  FALLBACK_RANKING_PERCENTAGES,
  toRankingPercentages,
  type RecommendationLabRankingPercentages,
} from '@/pages/recommendation-lab/rankingPolicy';
import {
  buildPresetSurveyAnswers,
  deriveSurveySkinType,
  isSurveyComplete,
  normalizeSurveyAnswers,
} from '@/pages/recommendation-lab/surveyAnswers';
import type { SavedValidationCase } from '@/pages/recommendation-lab/validation';

interface RecommendationLabInputColumnProps {
  inputOptions: RecommendationLabInputOptions | null;
  isInputOptionsLoading: boolean;
  inputOptionsError: string | null;
  onReloadInputOptions: () => unknown;
  isLoading: boolean;
  onRunValidation: (request: SkinAnalysisPredictValidationRequest) => Promise<unknown>;
  runLabel?: string;
}

function createValidationRequest(
  inputOptions: RecommendationLabInputOptions,
  metricScores: Record<SkinMetricKey, number>,
  surveyAnswers: Record<string, string[]>,
  environment: RecommendationEnvironmentFormValue,
): SkinAnalysisPredictValidationRequest {
  const questionsById = new Map(inputOptions.survey.questions.map((question) => [question.questionId, question]));
  const selectedAnswers = Object.entries(surveyAnswers).flatMap(([questionId, values]) => {
    const question = questionsById.get(questionId);
    if (!question || values.length === 0) {
      return [];
    }
    const labelsByValue = new Map(question.options.map((option) => [option.value, option.label]));
    // Canonical identifiers travel next to the labels so Facade can resolve policy inputs such as the age band.
    return [
      {
        question: question.title,
        values: values.map((value) => labelsByValue.get(value) ?? value),
        questionId: question.questionId,
        valueKeys: [...values],
      },
    ];
  });
  const builtEnvironment = buildRecommendationEnvironment(environment);
  return {
    metricScores,
    surveyAnswers: selectedAnswers,
    currency: Currency.USD,
    ...(builtEnvironment
      ? {
          environment: {
            ...(builtEnvironment.temperatureC !== undefined ? { temperatureC: builtEnvironment.temperatureC } : {}),
            ...(builtEnvironment.humidityPercent !== undefined
              ? { humidityPercent: builtEnvironment.humidityPercent }
              : {}),
            ...(builtEnvironment.uvIndex !== undefined ? { uvIndex: builtEnvironment.uvIndex } : {}),
            ...(builtEnvironment.season !== undefined ? { season: builtEnvironment.season } : {}),
            ...(builtEnvironment.airQuality?.index !== undefined ? { aqi: builtEnvironment.airQuality.index } : {}),
          },
        }
      : {}),
  };
}

export function RecommendationLabInputColumn({
  inputOptions,
  isInputOptionsLoading,
  inputOptionsError,
  onReloadInputOptions,
  isLoading,
  onRunValidation,
  runLabel = 'Run Product Selection',
}: RecommendationLabInputColumnProps) {
  const [metricScores, setMetricScores] = useState<Record<SkinMetricKey, number>>(DEFAULT_PRESET.metricScores);
  const [fallbackSkinType, setFallbackSkinType] = useState(DEFAULT_PRESET.skinType);
  const [surveyAnswers, setSurveyAnswers] = useState<Record<string, string[]>>({});
  const [environment, setEnvironment] = useState<RecommendationEnvironmentFormValue>(
    DEFAULT_RECOMMENDATION_ENVIRONMENT,
  );
  const [rankingPercentages, setRankingPercentages] =
    useState<RecommendationLabRankingPercentages>(FALLBACK_RANKING_PERCENTAGES);
  const [caseRestoreMessage, setCaseRestoreMessage] = useState<string>();
  const initializedSurveyKey = useRef<string | null>(null);
  const selectedPreset = useRef<LabPreset>(DEFAULT_PRESET);

  const surveyKey = useMemo(
    () =>
      inputOptions
        ? JSON.stringify([
            inputOptions.survey.surveyVersion,
            inputOptions.survey.questions.map((question) => [
              question.questionId,
              question.type,
              question.options.map((option) => option.value),
            ]),
          ])
        : null,
    [inputOptions],
  );
  const defaultRankingPercentages = useMemo(
    () => toRankingPercentages(inputOptions?.defaultRankingWeights),
    [inputOptions?.defaultRankingWeights],
  );

  useEffect(() => {
    if (!inputOptions || !surveyKey || initializedSurveyKey.current === surveyKey) {
      return;
    }
    initializedSurveyKey.current = surveyKey;
    setSurveyAnswers(buildPresetSurveyAnswers(selectedPreset.current, inputOptions));
    setRankingPercentages(defaultRankingPercentages);
    setCaseRestoreMessage(undefined);
  }, [defaultRankingPercentages, inputOptions, surveyKey]);

  const skinType = inputOptions
    ? deriveSurveySkinType(inputOptions, surveyAnswers, fallbackSkinType)
    : fallbackSkinType;
  const surveyComplete = inputOptions ? isSurveyComplete(inputOptions, surveyAnswers) : false;
  const inputIssues = [...collectRecommendationEnvironmentIssues(environment)];
  const applyPreset = (preset: LabPreset) => {
    selectedPreset.current = preset;
    setMetricScores(preset.metricScores);
    setFallbackSkinType(preset.skinType);
    if (inputOptions) {
      setSurveyAnswers(buildPresetSurveyAnswers(preset, inputOptions));
    }
  };

  const restoreCase = (saved: SavedValidationCase) => {
    if (!inputOptions) {
      return;
    }
    if (saved.surveyVersion !== inputOptions.survey.surveyVersion) {
      setCaseRestoreMessage(
        `Saved case ${saved.name} uses survey ${saved.surveyVersion} and cannot be restored into ${inputOptions.survey.surveyVersion}.`,
      );
      return;
    }
    const restoredSurveyAnswers = normalizeSurveyAnswers(inputOptions, saved.surveyAnswers);
    const restoredRankingPercentages = saved.rankingPercentages ?? defaultRankingPercentages;
    const restoredInputIssues = collectRecommendationEnvironmentIssues(saved.environment);
    setCaseRestoreMessage(undefined);
    setMetricScores(saved.metricScores);
    setSurveyAnswers(restoredSurveyAnswers);
    setEnvironment(saved.environment);
    setRankingPercentages(restoredRankingPercentages);
    if (isSurveyComplete(inputOptions, restoredSurveyAnswers) && restoredInputIssues.length === 0) {
      void onRunValidation(
        createValidationRequest(inputOptions, saved.metricScores, restoredSurveyAnswers, saved.environment),
      );
    } else if (restoredInputIssues.length > 0) {
      setCaseRestoreMessage(
        `The saved case was restored but not run: ${restoredInputIssues[0]?.message ?? 'Input error'}`,
      );
    }
  };

  return (
    <>
      <RecommendationLabCaseControls
        surveyVersion={inputOptions?.survey.surveyVersion ?? null}
        metricScores={metricScores}
        surveyAnswers={surveyAnswers}
        environment={environment}
        rankingPercentages={rankingPercentages}
        baselineResult={null}
        onApplyPreset={applyPreset}
        onRestoreCase={restoreCase}
      />

      {caseRestoreMessage && (
        <Alert variant="warning" className="small py-2">
          {caseRestoreMessage}
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Header className="py-2">Captured Skin Analysis Metrics</Card.Header>
        <Card.Body className="py-2">
          {METRICS.map((metric) => (
            <Form.Group key={metric.key} className="mb-2">
              <div className="d-flex justify-content-between small">
                <span>{metric.label}</span>
                <Badge bg={scoreVariant(metricScores[metric.key])}>{metricScores[metric.key]}</Badge>
              </div>
              <Form.Range
                aria-label={metric.label}
                min={0}
                max={100}
                value={metricScores[metric.key]}
                onChange={(event) => setMetricScores({ ...metricScores, [metric.key]: Number(event.target.value) })}
              />
            </Form.Group>
          ))}
          <div className="small text-muted">Skin type is derived by Facade from the active survey: {skinType}</div>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header className="py-2 d-flex justify-content-between align-items-center">
          <span>Active Skin Analysis Survey</span>
          <Badge bg="primary">
            {inputOptions?.survey.surveyVersion ?? '—'} · {inputOptions?.survey.questions.length ?? 0} questions
          </Badge>
        </Card.Header>
        <Card.Body className="py-2">
          {isInputOptionsLoading && (
            <div className="small text-muted">
              <Spinner size="sm" className="me-2" />
              Loading the survey schema…
            </div>
          )}
          {inputOptionsError && (
            <Alert variant="danger" className="small py-2 mb-0">
              Failed to load the survey schema: {inputOptionsError}{' '}
              <Button size="sm" variant="outline-danger" onClick={() => void onReloadInputOptions()}>
                Retry
              </Button>
            </Alert>
          )}
          {inputOptions && (
            <>
              <RecommendationSurveyForm
                inputOptions={inputOptions}
                answers={surveyAnswers}
                onChange={setSurveyAnswers}
              />
              {!surveyComplete && <div className="small text-danger">Complete every required survey question.</div>}
            </>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header className="py-2">Captured Weather, Season, and Air Quality</Card.Header>
        <Card.Body className="py-2">
          <div className="small text-muted mb-2">Uses the client result environment snapshot.</div>
          <RecommendationEnvironmentForm value={environment} onChange={setEnvironment} />
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body className="py-2">
          {inputIssues.length > 0 && (
            <Alert variant="danger" className="small py-2" id="recommendation-input-issues" role="alert">
              {inputIssues.map((issue) => (
                <div key={issue.code}>{issue.message}</div>
              ))}
            </Alert>
          )}
          <Button
            className="w-100"
            aria-describedby={inputIssues.length > 0 ? 'recommendation-input-issues' : undefined}
            disabled={isLoading || !inputOptions || !surveyComplete || inputIssues.length > 0}
            onClick={() => {
              if (!inputOptions) {
                return;
              }
              void onRunValidation(createValidationRequest(inputOptions, metricScores, surveyAnswers, environment));
            }}
          >
            {isLoading ? (
              <>
                <Spinner size="sm" className="me-2" />
                {runLabel}…
              </>
            ) : (
              runLabel
            )}
          </Button>
        </Card.Body>
      </Card>
    </>
  );
}
